import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import {
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppInputNumber,
  AppMultiSelect,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
} from '@/layout/AppComponents';
import { toFormData } from '../../../minions/forms';
import ModuleComsaSideBarReports from './ModuleComsaSideBarReports';
import Translator from '../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath } from '../../../routing/helpers/router';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';

export default function ModuleComsaBiodiversityScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState({});
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [reportFormInitialValues, setReportFormInitialValues] = useState({
    id: report.id ?? null,
    updateDate: report.updateDate ?? new Date().toLocaleDateString('es-ES'),
    locatedWithinOrNearSensitiveArea:
      report.locatedWithinOrNearSensitiveArea ?? false,
    affectedAreaType: report.affectedAreaType ?? [],
    affectedAreaNames: report.affectedAreaNames ?? {},
    protectedEndangeredSpecies: report.protectedEndangeredSpecies ?? false,
    protectedEndangeredSpeciesType: report.protectedEndangeredSpeciesType ?? [],
    protectedEndangeredSpeciesNames: report.protectedEndangeredSpeciesNames ?? {},
    kmAffected: null,
    apply: false,
    numberCopies: null,
  });
  const [reportType] = useState('biodiversity');
  const [moduleData, setModuleData] = useState(null);
  const affectedAreaType = [
    {
      label: 'Red Natura 2000',
      value: 'redNatura2000',
    },
    {
      label: 'Patrimonio Unesco',
      value: 'unesco',
    },
    {
      label: 'KBA- Área Clave Biodiversidad',
      value: 'kba',
    },
    {
      label: 'Otras zonas protegidas',
      value: 'otherProtectedAreas',
    },
  ];
  const protectedEndangeredSpeciesType = [
    {
      label: 'En Peligro (CR)',
      value: 'cr',
    },
    {
      label: 'En pelígro (EN)',
      value: 'en',
    },
    {
      label: 'Vulnerable (VU)',
      value: 'vu',
    },
    {
      label: 'Casi amenazada (NT)',
      value: 'nt',
    },
    {
      label: 'Preocupación menor (LC)',
      value: 'lc',
    },
  ];

  const loadReport = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_module_comsaReports_report'), {
        type: reportType,
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current && r.data.id) {
          setReportFormInitialValues(r.data);
          setReport(r.data);
        }
      })
      .finally(() => {
        if (isMounted.current) setLoading(false);
      });
  }, []);

  const loadModuleData = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_module_comsaReports_getModuleData'), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) setModuleData(r.data);
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);

  useEffect(() => {
    if (loading) {
      loadReport();
    }
  }, [loading]);

  const editReport = async (values) => {
    setIsSendingReport(true);
    await axios
      .post(
        axiosPath('api_r_user_module_comsaReports_editReport'),
        toFormData({
          ...values,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
          id: report?.id,
        }),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => {
        AppNotificationAlert({
          title: 'Conseguido',
          message: 'Biodiversidad actualizado',
          type: 'success',
          position: 'top-right',
          duration: 10000,
        });
        // setReportFormInitialValues(r.data.biodiversity);
        // setReport(r.data.biodiversity);
      })
      .finally(() => {
        if (isMounted.current) setIsSendingReport(false);
      });
  };
  // < EDIT REPORT

  if (!moduleData) return <AppNoInfo />;

  return (
    <div
      className="pl-2"
      title={ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="biodiversity" />,
      )}
    >
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData.plantName} - ${ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="evaluation" />,
                  )}`,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="indicators" />,
                  ),
                  route: {
                    name: 'user_module_comsaReports_home',
                    params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
                  },
                },
                {
                  name: (
                    <Translator file="module-comsaReports" name="biodiversity" />
                  ),
                },
              ]
            : [{ name: '...' }]
        }
      />
      <section className="content">
        <ModuleComsaSideBarReports
          activeRoute="user_module_comsaReports_biodiversity"
          moduleData={moduleData}
        />
        <div className="row">
          <div
            className="col-12"
            title={ReactDOMServer.renderToString(
              <Translator file="module-comsaReports" name="biodiversity" />,
            )}
          >
            <Title>
              <Translator file="module-comsaReports" name="biodiversity" />
            </Title>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <div>
                    <Translator file="module-saicaReports" name="loading" />
                  </div>
                ) : (
                  <Formik
                    validateOnChange={false}
                    validateOnBlur={false}
                    enableReinitialize
                    initialValues={reportFormInitialValues}
                    // validationSchema={validationSchema}
                    onSubmit={editReport}
                  >
                    {({ setFieldValue, handleSubmit, values, errors }) => (
                      <>
                        <div className="row">
                          <div className="col-2 text-center">
                            <div className="mb-3">
                              <AppDatePicker
                                title={
                                  <Translator
                                    file="module-comsaReports"
                                    name="update.date"
                                  />
                                }
                                value={values.updateDate}
                                onChange={(opt) => setFieldValue('updateDate', opt)}
                                minDate={new Date(2021, 0, 1)}
                                isClearable
                              />
                              {errors.updateDate && (
                                <div className="text-danger">
                                  *{errors.updateDate}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-3 text-center">
                            <AppSingleSelect
                              options={[
                                {
                                  label: (
                                    <Translator
                                      file="module-comsaReports"
                                      name="module-comsaReports.no"
                                    />
                                  ),
                                  value: false,
                                },
                                {
                                  label: (
                                    <Translator
                                      file="module-comsaReports"
                                      name="module-comsaReports.yes"
                                    />
                                  ),
                                  value: true,
                                },
                              ]}
                              title={
                                <Translator
                                  file="module-comsaReports"
                                  name="module-comsaReports.near.sensitive.area"
                                />
                              }
                              value={values?.locatedWithinOrNearSensitiveArea}
                              error={errors?.locatedWithinOrNearSensitiveArea}
                              onChange={(opt) =>
                                setFieldValue('locatedWithinOrNearSensitiveArea', opt)
                              }
                            />
                          </div>

                          <div className="col-1 text-center">
                            <AppSingleSelect
                              options={[
                                {
                                  label: (
                                    <Translator
                                      file="module-comsaReports"
                                      name="module-comsaReports.no"
                                    />
                                  ),
                                  value: false,
                                },
                                {
                                  label: (
                                    <Translator
                                      file="module-comsaReports"
                                      name="module-comsaReports.yes"
                                    />
                                  ),
                                  value: true,
                                },
                              ]}
                              title={
                                <Translator
                                  file="module-comsaReports"
                                  name="module-comsaReports.apply"
                                />
                              }
                              value={values?.apply}
                              error={errors?.apply}
                              onChange={(opt) => setFieldValue('apply', opt)}
                            />
                          </div>

                          <div className="col-3 text-center">
                            <AppMultiSelect
                              options={affectedAreaType}
                              title={
                                <Translator
                                  file="module-comsaReports"
                                  name="module-comsaReports.affected.area"
                                />
                              }
                              value={values?.affectedAreaType}
                              error={errors?.affectedAreaType}
                              onChange={(opt) => {
                                const selectedValues = opt || [];

                                setFieldValue('affectedAreaType', selectedValues);

                                const cleanedAffectedAreaNames = Object.fromEntries(
                                  Object.entries(
                                    values?.affectedAreaNames || {},
                                  ).filter(([key]) => selectedValues.includes(key)),
                                );

                                setFieldValue(
                                  'affectedAreaNames',
                                  cleanedAffectedAreaNames,
                                );
                              }}
                              isClearable
                            />
                          </div>

                          <div className="col-3 text-center">
                            {values?.affectedAreaType?.map((areaValue) => {
                              const area = affectedAreaType.find(
                                (item) => item.value === areaValue,
                              );

                              if (!area) return null;

                              return (
                                <AppInput
                                  key={area.value}
                                  title={
                                    <>
                                      <Translator
                                        file="module-comsaReports"
                                        name="module-comsaReports.name.appropriate"
                                      />{' '}
                                      - {area.label}
                                    </>
                                  }
                                  value={
                                    values?.affectedAreaNames?.[area.value] || ''
                                  }
                                  error={errors?.affectedAreaNames?.[area.value]}
                                  onChange={(v) =>
                                    setFieldValue(
                                      `affectedAreaNames.${area.value}`,
                                      v,
                                    )
                                  }
                                  isClearable
                                />
                              );
                            })}
                          </div>

                          <div className="col-2 text-center">
                            <AppInputNumber
                              title={
                                <Translator
                                  file="module-comsaReports"
                                  name="module-comsaReports.kilometres.affected"
                                />
                              }
                              value={values.kmAffected}
                              error={errors?.kmAffected}
                              onChange={(v) => setFieldValue('kmAffected', v)}
                              isClearable
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-3 text-center">
                            <AppSingleSelect
                              options={[
                                {
                                  label: (
                                    <Translator
                                      file="module-comsaReports"
                                      name="module-comsaReports.no"
                                    />
                                  ),
                                  value: false,
                                },
                                {
                                  label: (
                                    <Translator
                                      file="module-comsaReports"
                                      name="module-comsaReports.yes"
                                    />
                                  ),
                                  value: true,
                                },
                              ]}
                              title={
                                <Translator
                                  file="module-comsaReports"
                                  name="module-comsaReports.protected.endangered.species"
                                />
                              }
                              value={values?.protectedEndangeredSpecies}
                              error={errors?.protectedEndangeredSpecies}
                              onChange={(opt) =>
                                setFieldValue('protectedEndangeredSpecies', opt)
                              }
                            />
                          </div>

                          <div className="offset-1 col-3 text-center">
                            <AppMultiSelect
                              options={protectedEndangeredSpeciesType}
                              title={
                                <Translator
                                  file="module-comsaReports"
                                  name="module-comsaReports.select.option"
                                />
                              }
                              value={values?.protectedEndangeredSpeciesType}
                              error={errors?.protectedEndangeredSpeciesType}
                              onChange={(opt) => {
                                const selectedValues = opt || [];

                                setFieldValue(
                                  'protectedEndangeredSpeciesType',
                                  selectedValues,
                                );

                                const cleanedProtectedEndangeredSpeciesNames =
                                  Object.fromEntries(
                                    Object.entries(
                                      values?.protectedEndangeredSpeciesNames || {},
                                    ).filter(([key]) => selectedValues.includes(key)),
                                  );

                                setFieldValue(
                                  'protectedEndangeredSpeciesNames',
                                  cleanedProtectedEndangeredSpeciesNames,
                                );
                              }}
                              isClearable
                            />
                          </div>

                          <div className="col-3 text-center">
                            {values?.protectedEndangeredSpeciesType?.map(
                              (speciesValue) => {
                                const species = protectedEndangeredSpeciesType.find(
                                  (item) => item.value === speciesValue,
                                );

                                if (!species) return null;

                                return (
                                  <AppInput
                                    key={species.value}
                                    title={
                                      <>
                                        <Translator
                                          file="module-comsaReports"
                                          name="module-comsaReports.species"
                                        />
                                        {` - ${species.label}`}
                                      </>
                                    }
                                    value={
                                      values?.protectedEndangeredSpeciesNames?.[
                                        species.value
                                      ] || ''
                                    }
                                    error={
                                      errors?.protectedEndangeredSpeciesNames?.[
                                        species.value
                                      ]
                                    }
                                    onChange={(v) =>
                                      setFieldValue(
                                        `protectedEndangeredSpeciesNames.${species.value}`,
                                        v,
                                      )
                                    }
                                    isClearable
                                  />
                                );
                              },
                            )}
                          </div>

                          <div className="col-2 text-center">
                            <AppInputNumber
                              title={
                                <Translator
                                  file="module-comsaReports"
                                  name="module-comsaReports.number.copies"
                                />
                              }
                              value={values.numberCopies}
                              error={errors?.numberCopies}
                              onChange={(v) => setFieldValue('numberCopies', v)}
                              isClearable
                            />
                          </div>
                        </div>

                        <div className="row mt-3">
                          <div className="col-12">
                            <button
                              className="btn btn-success float-right"
                              type="submit"
                              onClick={handleSubmit}
                              disabled={isSendingReport}
                            >
                              <Translator file="module-comsaReports" name="edit" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </Formik>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;
